import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';
import hotSpotIcon from '@assets/images/hotspot-icon.svg';
import HotelDetails from '@customTypes/hotel-details';
import Currency from '@components/common/Currrency/Currency';
import { differenceInMilliseconds, parseISO } from 'date-fns';
import useHotSpot from '@hooks/useHotSpot';
import HotSpotCountdown from './HotSpotCountdown';

type Props = {
  hotel: HotelDetails;
};

export default function HotSpotBanner({ hotel }: Props) {
  const [showCountdown, setShowCountdown] = useState<boolean>(false);

  const { convertToPSTAndFormat, formatDays } = useHotSpot();

  // Function to calculate the time difference
  const calculateDifference = useCallback(() => {
    if (!hotel.hot_spot) return;

    const endDate = parseISO(hotel.hot_spot.end_booking_date);
    const now = new Date();
    const differenceInMs = differenceInMilliseconds(endDate, now);

    // Convert milliseconds to days
    const msInDay = 1000 * 60 * 60 * 24;
    const daysDifference = differenceInMs / msInDay;

    // Set showCountdown based on the difference in days
    setShowCountdown(daysDifference < 3 && differenceInMs >= 0);
  }, [hotel.hot_spot]);

  useEffect(() => {
    calculateDifference();
  }, [calculateDifference]);

  return hotel.hot_spot ? (
    <div className="mt-8 px-4 w-full d:px-0 d:mb-4">
      <div className="bg-rp-hot-spot px-6 pb-18px pt-22px rounded-t-4 relative">
        {showCountdown && hotel.hot_spot ? (
          <HotSpotCountdown targetDate={parseISO(hotel.hot_spot.end_booking_date)} />
        ) : (
          <div>
            <div
              className="absolute right-0"
              style={{ height: '71px', width: '88px', top: '20px', right: '10px' }}
            >
              <Image src={hotSpotIcon} alt="Hot Spot Icon" layout="fill" />
            </div>

            <p className="text-white text-base font-semibold leading-5 max-w-204 -tracking-0.32 d:max-w-none">
              Exclusive ResortPass savings for a limited time only
            </p>
          </div>
        )}
      </div>

      <div className="flex flex-col pb-6 pt-4 px-6 rounded-b-4 shadow-top-spot">
        <p className="text-rp-neutral-900 text-xl -tracking-0.4">
          {hotel.hot_spot.discount_type === 'cash' ? (
            <Currency
              value={hotel.hot_spot.discount}
              isoCodeClasses="text-rp-neutral-900 text-xl -tracking-0.4"
              valueClasses="text-rp-neutral-900 text-xl -tracking-0.4"
            />
          ) : (
            `${hotel.hot_spot.discount}%`
          )}{' '}
          off select products
        </p>

        <p className="mt-1 text-rp-text-secondary text-xs leading-14px">
          For visits between {convertToPSTAndFormat(hotel.hot_spot.start_visit_date)} -{' '}
          {convertToPSTAndFormat(hotel.hot_spot.end_visit_date)} PT.{' '}
          {formatDays(hotel.hot_spot.days_of_the_week || [])}No code required.{' '}
          <a
            href="https://www.resortpass.com/terms-conditions"
            target="_blank"
            className=" text-rp-text-secondary"
            rel="noreferrer"
          >
            Terms and Conditions
          </a>{' '}
          apply.
        </p>
      </div>
    </div>
  ) : null;
}
